import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Page name mapping
const pageNameMap = {
  '/': 'landing',
  '/dashboard': 'dashboard',
  '/calculator': 'calculator',
  '/mdb': 'mdb',
  '/ai-agent': 'ai_agent',
  '/ai-stats': 'ai_stats',
  '/cadastre': 'cadastre',
  '/diagnostic-climatique': 'diagnostic_climatique',
  '/community': 'community',
  '/messages': 'messages',
  '/classroom': 'classroom',
  '/properties': 'properties',
  '/events': 'events',
  '/profile': 'profile',
  '/directory': 'directory',
  '/pros': 'pros_directory',
  '/pricing': 'pricing',
  '/integrations': 'integrations',
  '/blog': 'blog'
};

const getPageName = (path) => {
  if (pageNameMap[path]) return pageNameMap[path];
  if (path.startsWith('/blog/')) return 'blog_article';
  return path.replace(/^\//, '').split('/')[0].replace(/-/g, '_') || 'landing';
};

const postEvent = async (isAuthenticated, payload) => {
  try {
    if (isAuthenticated) {
      await axios.post(`${API}/api/analytics/track`, payload, { withCredentials: true });
    } else {
      await axios.post(`${API}/api/analytics/track/anonymous`, payload);
    }
  } catch (e) {
    console.debug('Analytics tracking failed:', e.message);
  }
};

// Mounted once in App: records every page view (members + anonymous visitors)
export const usePageViewTracking = (user, authLoading) => {
  const location = useLocation();
  const isAuthenticated = !!user;

  useEffect(() => {
    if (authLoading) return;
    if (location.pathname.startsWith('/admin')) return;
    postEvent(isAuthenticated, {
      event_type: 'page_view',
      event_name: getPageName(location.pathname),
      page: location.pathname,
      metadata: { path: location.pathname, referrer: document.referrer || null }
    });
  }, [location.pathname, authLoading, isAuthenticated]);
};

export const useAnalytics = (isAuthenticated = true) => {
  const location = useLocation();

  const trackEvent = useCallback((eventType, eventName, metadata = {}, action = null, duration = null) => {
    postEvent(isAuthenticated, {
      event_type: eventType,
      event_name: eventName,
      page: location.pathname,
      action,
      metadata,
      duration_ms: duration
    });
  }, [location.pathname, isAuthenticated]);

  const trackToolUse = useCallback((toolName, action = null, metadata = {}) => {
    trackEvent('tool_use', toolName, metadata, action);
  }, [trackEvent]);

  const trackAction = useCallback((actionName, metadata = {}) => {
    trackEvent('action', getPageName(location.pathname), metadata, actionName);
  }, [trackEvent, location.pathname]);

  const trackError = useCallback(async (errorType, errorMessage, endpoint = null) => {
    try {
      await axios.post(`${API}/api/analytics/error`, {
        error_type: errorType,
        error_message: errorMessage,
        endpoint
      });
    } catch (e) {
      console.debug('Error tracking failed');
    }
  }, []);

  return { trackEvent, trackToolUse, trackAction, trackError };
};

export default useAnalytics;
